/**
 * Council Trail Task 6.2 — CrewStrip.
 *
 * Row of persona dots (one per active peer) followed by the operator
 * marker. Renders inside TrailheadHeader row-1 when councilMode is true.
 * Dismissed peers are filtered out — they don't take a slot in the strip.
 *
 * Dots use the persona's resolved color (accent_color → session color_map
 * → name-hash). When CouncilProvider has hydrated personasById, the dot
 * gets the full color. While hydrating, dots fall back to a neutral
 * gray (no flash of colored dots that then re-render).
 */
import React, { useContext } from 'react';

import { CouncilContext } from '../../contexts/CouncilContext';
import { LensContext } from '../../contexts/LensContext';
import { getPersonaColor } from '../../utils/personaColor';
import { TrailmasterCrown } from '../personas/TrailmasterCrown';

export interface CrewStripRosterRow {
  /** M001 row id. */
  id: string;
  /** Persona uuid — keys the personasById lookup. */
  persona_id: string;
  /** ISO timestamp; presence means dismissed (peer hidden from strip). */
  dismissed_at?: string | null;
}

export interface CrewStripProps {
  roster: CrewStripRosterRow[];
  operatorMarker: { displayName: string };
  fadeInOnMount?: boolean;
}

export function CrewStrip({
  roster,
  operatorMarker,
  fadeInOnMount,
}: CrewStripProps) {
  const active = roster.filter(r => !r.dismissed_at);
  return (
    <div
      data-testid="crew-strip"
      className={`flex items-center gap-1.5 shrink-0 ${
        fadeInOnMount ? 'transition-opacity duration-200' : ''
      }`}
    >
      {active.map(entry => (
        <PersonaDot key={entry.id} personaId={entry.persona_id} />
      ))}
      {active.length > 0 && (
        <span className="text-gray-400 dark:text-gray-500">·</span>
      )}
      <span
        data-testid="crew-operator-marker"
        className="text-gray-700 dark:text-gray-200 text-xs"
      >
        ◼ {operatorMarker.displayName}
      </span>
    </div>
  );
}

function PersonaDot({ personaId }: { personaId: string }) {
  const ctx = useContext(CouncilContext);
  const persona = ctx?.state?.personasById?.[personaId];
  const colorMap = ctx?.state?.metadata?.persona_color_map;
  // SP3 I.4 — Trailmaster prefix marker. Read core_persona_id directly
  // off the same CouncilContext (avoids the usePersonaSessionRoles
  // re-context lookup since we already have ctx here).
  const trailmasterPersonaId = ctx?.state?.metadata?.core_persona_id
    ? String(ctx.state.metadata.core_persona_id)
    : null;
  const isTrailmaster = trailmasterPersonaId === personaId;
  const color = persona
    ? getPersonaColor(
        { id: persona.id, accent_color: persona.accent_color, name: persona.name },
        colorMap,
      )
    : null;
  // Tailwind class names from getPersonaColor (`light: 'indigo-500'` etc) are
  // *static text* from the perspective of Tailwind's JIT — they're never seen
  // as `text-indigo-500` etc, so the JIT purges them. Use inline color to
  // avoid the JIT-purge trap (same lesson as Task 6.6 PeekStripBlock).
  const HEX_BY_HUE: Record<string, string> = {
    indigo: '#6366f1', cyan: '#06b6d4', violet: '#8b5cf6', rose: '#f43f5e',
    lime: '#84cc16', fuchsia: '#d946ef', sky: '#0ea5e9', orange: '#f97316',
    teal: '#14b8a6', pink: '#ec4899',
  };
  const hex = color ? HEX_BY_HUE[color.hue] : '#9ca3af';

  // P3 SP1 — with a lens in scope the dot becomes a toggle: solid = on-lens,
  // hollow ring at full color = off (the standby-marker visual language —
  // NOT a low-opacity dot, which is illegible on dark chrome). Mode derives
  // from the set inside setLens (0=canopy/1=single/2+=multi; exits trail).
  // No LensContext (admin/non-council surfaces + legacy suites) → the exact
  // display-only span below, unchanged.
  const lens = useContext(LensContext);
  if (lens) {
    const set = lens.activePersonaIds;
    const member = set.size > 0 && set.has(personaId);
    const lit = set.size === 0 || member; // empty set = canopy = everyone lit
    const toggle = () => {
      const next = new Set(lens.activePersonaIds);
      if (next.has(personaId)) next.delete(personaId); else next.add(personaId);
      lens.setLens({ activePersonaIds: next });
    };
    return (
      <button
        type="button"
        data-testid={`crew-dot-button-${personaId}`}
        aria-pressed={lit}
        aria-label={`Focus ${persona?.name ?? personaId}`}
        onClick={toggle}
        title={persona?.name ?? personaId}
        className="p-1 rounded hover:bg-gray-200/60 dark:hover:bg-gray-700/40"
      >
        {isTrailmaster && <TrailmasterCrown variant="inline-prefix" />}
        <span
          data-testid={`crew-dot-${personaId}`}
          aria-hidden
          style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            color: hex,
            ...(lit
              ? { background: hex }
              : { background: 'transparent', border: `1.5px solid ${hex}` }),
          }}
        />
      </button>
    );
  }

  return (
    <span
      data-testid={`crew-dot-${personaId}`}
      className="text-xs"
      style={{ color: hex }}
      title={persona?.name ?? personaId}
    >
      {isTrailmaster && <TrailmasterCrown variant="inline-prefix" />}
      ●
    </span>
  );
}
